// src/app/pages/dashboard/Apps/serviceComponents/BrandScoresChart.tsx

import React, { useState, useEffect } from 'react';
import { Chart } from 'primereact/chart';

interface BrandScore {
  brand: string;
  avgScore: number;
}

interface BrandScoresChartProps {
  brandScores?: BrandScore[];
}

const BrandScoresChart: React.FC<BrandScoresChartProps> = ({ brandScores = [] }) => {
  // States for the summary (bins) chart
  const [chartData, setChartData] = useState<any>(null);
  const [chartOptions, setChartOptions] = useState<any>(null);

  // States for the detail (brands) chart
  const [detailChartData, setDetailChartData] = useState<any>(null);
  const [detailChartOptions, setDetailChartOptions] = useState<any>(null);

  // State to store the currently selected category label and its filtered brands
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedBrands, setSelectedBrands] = useState<BrandScore[]>([]);

  useEffect(() => {
    // Retrieve PrimeReact CSS variables for theming
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color') || '#000';
    const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary') || '#666';
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border') || '#ccc';

    // Define bins and their labels for the summary chart
    const labels = [
      'Poor (≤50)',
      'Average (50–70)',
      'Good (70–80)',
      'Excellent (≥81)'
    ];

    // Count the number of brands falling into each bin
    const poorCount = brandScores.filter(score => score.avgScore <= 50).length;
    const averageCount = brandScores.filter(score => score.avgScore > 50 && score.avgScore < 70).length;
    const goodCount = brandScores.filter(score => score.avgScore >= 70 && score.avgScore < 80).length;
    const excellentCount = brandScores.filter(score => score.avgScore >= 80).length;

    const dataValues = [poorCount, averageCount, goodCount, excellentCount];

    // Define a set of colors for the summary bins
    const barColors = ['#FF4560', '#FFA600', '#00E396', '#008FFB'];

    // Build summary chart data for a horizontal bar chart
    const data = {
      labels,
      datasets: [
        {
          label: "Brand Scores",
          data: dataValues,
          backgroundColor: barColors,
          borderColor: barColors,
          borderWidth: 1,
        },
      ],
    };

    // Handler for when a summary bar is clicked
    const handleBarClick = (index: number) => {
      let categoryLabel = labels[index];
      setSelectedCategory(categoryLabel);
      let filteredBrands: BrandScore[] = [];
      if (index === 0) {
        filteredBrands = brandScores.filter(score => score.avgScore <= 50);
      } else if (index === 1) {
        filteredBrands = brandScores.filter(score => score.avgScore > 50 && score.avgScore < 70);
      } else if (index === 2) {
        filteredBrands = brandScores.filter(score => score.avgScore >= 70 && score.avgScore < 80);
      } else if (index === 3) {
        filteredBrands = brandScores.filter(score => score.avgScore >= 80);
      }
      setSelectedBrands(filteredBrands);
    };

    // Define options for the summary chart (horizontal bar chart)
    const options = {
      indexAxis: 'y',
      maintainAspectRatio: false,
      aspectRatio: 0.8,
      // onClick receives the event and an array of active elements
      onClick: (event: any, elements: any[]) => {
        if (elements && elements.length > 0) {
          const index = elements[0].index;
          handleBarClick(index);
        }
      },
      plugins: {
        legend: {
          labels: { color: textColor },
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
            font: { weight: 500 },
          },
          grid: { display: false, drawBorder: false },
        },
        y: {
          ticks: { color: textColorSecondary },
          grid: { color: surfaceBorder, drawBorder: false },
        },
      },
    };

    setChartData(data);
    setChartOptions(options);
  }, [brandScores]);

  // Update detail chart whenever selectedBrands changes
  useEffect(() => {
    if (selectedBrands.length === 0) {
      setDetailChartData(null);
      setDetailChartOptions(null);
      return;
    }

    // Retrieve theme variables for the detail chart as well
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color') || '#000';
    const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary') || '#666';
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border') || '#ccc';

    // Prepare labels and values for each brand in the selected category
    const labels = selectedBrands.map(score => score.brand || 'Unknown');
    const dataValues = selectedBrands.map(score => score.avgScore);

    // Generate distinct colors for each brand using HSL so that they are evenly distributed
    const barColors = selectedBrands.map((_, idx) => {
      const hue = Math.round((360 / selectedBrands.length) * idx);
      return `hsl(${hue}, 70%, 50%)`;
    });

    const data = {
      labels,
      datasets: [
        {
          label: "Average Score",
          data: dataValues,
          backgroundColor: barColors,
          borderColor: barColors,
          borderWidth: 1,
        },
      ],
    };

    // In the detail chart, we set the x-axis maximum to 100 since scores are out of 100.
    const options = {
      indexAxis: 'y',
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: { color: textColor },
        },
      },
      scales: {
        x: {
          max: 100,
          ticks: {
            color: textColorSecondary,
            font: { weight: 500 },
          },
          grid: { display: false, drawBorder: false },
        },
        y: {
          ticks: { color: textColorSecondary },
          grid: { color: surfaceBorder, drawBorder: false },
        },
      },
    };

    setDetailChartData(data);
    setDetailChartOptions(options);
  }, [selectedBrands]);

  // Fallback UI if no data is available
  if (!brandScores.length) {
    return <div>No Brand Score data available.</div>;
  }

  // Dynamically adjust detail chart container height: e.g. 40px per brand, with a minimum height
  const detailChartHeight = selectedBrands.length ? Math.max(350, selectedBrands.length * 40) : 350;

  return (
    <div>
      <div className="card" style={{ height: '350px', marginBottom: '2rem' }}>
        {chartData && chartOptions && (
          <Chart type="bar" data={chartData} options={chartOptions} />
        )}
      </div>
      {selectedCategory && detailChartData  && (
        <div className="card" style={{ paddingTop: '3rem' , marginBottom: '2rem' }}>
          <h3 style={{ textAlign: 'center' }}>{selectedCategory} Brands</h3>
          <Chart type="bar" data={detailChartData} />
        </div>
      )}
    </div>
  );
};

export default BrandScoresChart;
